'use client';

import type { ReactNode } from 'react';

import { Flex, Group } from '@highlight-cp/core-ui';

/**
 * The field's own line, in the row's coordinates: the label block above it is the offset, the
 * field's height is what there is to centre in. Both are theme tokens
 * (tools/theme/src/css-variables-resolver.ts) because Mantine keeps its input metrics on an
 * element only the field itself can see.
 *
 * `lh={1}` because a tooltip wraps its target in an inline-level `.reference` element, which
 * otherwise inherits the body line-height and stands taller than the button inside it — the grip
 * and the bin of the same row land 2.5px apart without it.
 */
const OnFieldLine = ({
  children,
  size,
}: {
  children: ReactNode;
  size: NonNullable<FieldRowProps['size']>;
}) => (
  <Flex align="center" h={`var(--hl-field-height-${size})`} lh={1} mt="var(--hl-field-label-block)">
    {children}
  </Flex>
);

export interface FieldRowProps {
  /** The labelled fields, laid out in a row. Size them with their own props (`flex`, `w`). */
  children: ReactNode;
  /** Buttons that follow the fields — a delete, say. */
  actions?: ReactNode;
  /** Buttons that come before the fields — a drag grip, say. */
  leading?: ReactNode;
  /**
   * The size the row's fields are rendered at, which is what the buttons align against. Only the
   * sizes with a `--hl-field-height-*` token can be aligned to.
   */
  size?: 'xs' | 'sm';
}

/**
 * ### FieldRow
 *
 * @description
 * A row of labelled fields with buttons beside them — the shape an editable list's row takes: a
 * grip, the fields, a bin.
 *
 * A labelled field is taller than the box the user sees: a label sits above it, and a validation
 * error appears below. Aligning a row of them by their edges therefore puts a button on the edge of
 * *that* block rather than on the field, and moves it again the moment an error renders. This row
 * aligns from the top and sits `leading`/`actions` on the field's own line, so they hold their
 * place whatever the fields are doing.
 *
 * Fields keep their labels — a caller wanting one header row above a list of rows wants a table,
 * not this. `size` must match what the fields are given; `--hl-field-height-*` is where a size that
 * is not yet listed would go.
 *
 * @example
 * ```tsx
 * <FieldRow
 *   leading={<ActionIcons.Drag ariaLabel={t('Reorder')} buttonSize="sm" />}
 *   actions={<ActionIcons.Delete ariaLabel={t('Remove')} buttonSize="sm" />}
 * >
 *   <Select flex={1} label={t('Product')} data={options} />
 *   <NumberInput label={t('Units')} w={120} />
 * </FieldRow>
 * ```
 *
 * @param props - {@link FieldRowProps}
 * @returns React component
 */
export const FieldRow = ({ actions, children, leading, size = 'sm' }: FieldRowProps) => (
  <Group align="flex-start" gap="sm" wrap="nowrap">
    {leading && <OnFieldLine size={size}>{leading}</OnFieldLine>}
    {children}
    {actions && <OnFieldLine size={size}>{actions}</OnFieldLine>}
  </Group>
);
